import {
  Button,
  Backdrop,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Theme,
  Typography,
  Divider,
} from '@mui/material';
import { createStyles, makeStyles, useTheme } from '@mui/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import logo from '../assets/images/logo_secom.svg';
import ErrorModal from '../components/ErrorModal';
import { formValidations } from '../constants/validations';
import TextModal from '../components/TextModal';
import { MESSAGES } from '../assets/messages';
import { fillForm, ping } from '../providers/form.service';
import { IFormDTO } from '../interfaces/form.interface';
import SuccessModal from '../components/SuccessModal';
import { useSearchParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { modalType } from '../constants/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: 'relative',
      display: 'flex !important',
      alignItems: 'center',
      flexDirection: 'column',
      marginTop: '1rem',
      padding: 0,
      paddingTop: '1rem',
    },
    toolbar: {
      justifyContent: 'center',
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      width: '80%',
      height: '100%',
      padding: '2rem',
      paddingTop: 0,
      paddingBottom: 0,
      alignItems: 'center',
      justifyContent: 'start',
      margin: '0',
      marginTop: '0rem',
      overflow: 'visible !important',
    },

    cardContent: {
      width: '80%',
      paddingTop: '0!important',
      paddingBottom: '0!important',
    },
    formGrid: {
      width: '100%',
      padding: '1rem 0!important',
    },
    input: {
      width: '100%',
    },
    subInput: {
      width: '50%',
    },
    formButton: {
      width: '100%',
    },
    labelText: {
      color: theme.palette.primary.main,
    },
    toolbarButton: {
      width: '5rem',
      backgroundColor: 'white!important',
    },
    logoContainer: {
      flexDirection: 'column',
      display: 'flex',
      justifyContent: 'center',
      flexGrow: 1,
      margin: '0.5rem',
      gap: '1rem',
      alignItems: 'center',
    },
    logo: {
      position: 'absolute',
      top: '1.25rem',
      left: '14rem',
      height: '3rem',
      [theme.breakpoints.down(1200)]: {
        position: 'relative',
        top: 'unset',
        left: 'unset',
      },
    },
    logoOuterContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    toolbarButtonContainer: {
      position: 'absolute',
      right: '24px',
    },
    checkLabel: {
      margin: 0,
    },
    location: {
      fontSize: '0.9rem !important',
      fontWeight: '500 !important',
      paddingLeft: '6px !important',
      paddingRight: '6px !important',
    },
  }),
);

const CreateMembership = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [postError, setPostError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [showError, setShowError] = useState<boolean>(false);
  const [canvasError, setCanvasError] = useState<boolean>(false);
  const signCanvasRef = useRef<SignatureCanvas>(null);
  const canvasParentRef = useRef<HTMLDivElement>(null);
  const {
    handleSubmit,
    control,
    reset,

    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const queryParamValues = useMemo(() => {
    const params: Partial<IFormDTO> & { hmac?: string; timestamp?: string } =
      {};

    // Get string fields
    const firstName = searchParams.get('firstName');
    const lastName = searchParams.get('lastName');
    const phone = searchParams.get('phone');
    const email = searchParams.get('email');
    const medicId = searchParams.get('medicId');
    const cuim = searchParams.get('cuim');
    const hmac = searchParams.get('hmac');
    const timestamp = searchParams.get('timestamp');
    const returnUrl = searchParams.get('returnUrl');

    // Add string fields to params if they exist
    if (firstName) params.firstName = firstName;
    if (lastName) params.lastName = lastName;
    if (phone) params.phone = phone;
    if (email) params.email = email;
    if (medicId) params.medicId = medicId;
    if (cuim) params.cuim = cuim;
    if (hmac) params.hmac = hmac;
    if (timestamp) params.timestamp = timestamp;
    if (returnUrl) params.returnUrl = returnUrl;

    if (
      !params?.firstName ||
      !params?.lastName ||
      !params?.phone ||
      !params?.email ||
      !params?.medicId ||
      !params?.hmac ||
      !params?.timestamp
    ) {
      setShowError(true);
    }

    return params;
  }, [searchParams]);

  useEffect(() => {
    if (queryParamValues.hmac && queryParamValues.timestamp) {
      setIsLoading(true);
      ping(queryParamValues.hmac, queryParamValues.timestamp)
        .then((res: any) => {
          // moving on
        })
        .catch(() => {
          setPostError('403');
          setShowError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [queryParamValues.hmac, queryParamValues.timestamp]);

  // Example URL to populate all fields
  // http://localhost:3000/?hmac=5cf13f1396b41409cfaf6dc449a0157b6801661edba752c63f389867d4efa48c&timestamp=1&medicId=987654321&firstName=Jane&lastName=Smith&phone=5551234567&email=jane.smith@example.com&inviteCommercial=true&inviteMedical=false&emailMarketing=true&smsMarketing=false

  // Initialize form with query parameters if they exist
  useEffect(() => {
    if (Object.keys(queryParamValues).length > 0) {
      reset(queryParamValues);
    }
  }, [reset, queryParamValues]);

  const onReset = () => {
    reset(queryParamValues);
    clearErrors();
    signCanvasRef.current?.clear();
  };

  const onSubmit = (data: any) => {
    if (signCanvasRef.current?.isEmpty()) {
      setCanvasError(true);
      return;
    } else {
      setCanvasError(false);
    }

    if (
      !queryParamValues.firstName ||
      !queryParamValues.lastName ||
      !queryParamValues.phone ||
      !queryParamValues.timestamp ||
      !queryParamValues.hmac ||
      !queryParamValues.medicId ||
      !queryParamValues.email
    ) {
      return;
    }

    setIsLoading(true);

    const payload: IFormDTO = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      medicalInviteCommercial: data.medicalInviteCommercial,
      medicalInviteMedical: data.medicalInviteMedical,
      medicalEmailMarketing: data.medicalEmailMarketing,
      medicalSmsMarketing: data.medicalSmsMarketing,
      timestamp: queryParamValues.timestamp,
      medicId: queryParamValues.medicId,
      cuim: queryParamValues.cuim,
      medicalTos: data.medicalTos,
    };

    const dataURL = signCanvasRef.current?.toDataURL();
    const byteString = atob(dataURL?.split(',')[1] || '');
    const mimeString = dataURL?.split(',')[0].split(':')[1].split(';')[0] || '';

    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([uint8Array], { type: mimeString });
    const file = new File([blob], 'signature-image.png', {
      type: mimeString,
    });

    fillForm(payload, queryParamValues.hmac, file)
      .then(() => {
        setIsLoading(false);
        setSuccess(true);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error?.response?.data?.message) {
          if (error?.response.data.status === 'T_COMMON_KLAVYIO_004') {
            setPostError(MESSAGES['klaviyo.post.error']);
          } else {
            setPostError(error?.response?.data?.message);
          }
        } else {
          if (!navigator.onLine) {
            setPostError(MESSAGES[`error.internet.access` as string]);
          } else {
            setPostError(MESSAGES[`error.500` as string]);
          }
        }
      });
  };

  const onCloseSuccessModal = () => {
    setSuccess(false);
    goToReturnUrl();
  };

  const goToReturnUrl = () => {
    if (queryParamValues.returnUrl) {
      window.location.href = queryParamValues.returnUrl;
      return;
    }
  };

  const validateCanvas = () => {
    if (signCanvasRef.current?.isEmpty()) {
      setCanvasError(true);
    } else {
      setCanvasError(false);
    }
  };

  const onResetSignature = () => {
    signCanvasRef.current?.clear();
    setCanvasError(false);
  };

  if (showError) {
    return (
      <Typography variant="h1" style={{ textAlign: 'center' }}>
        403 Forbidden
      </Typography>
    );
  }

  return (
    <>
      <Backdrop open={isLoading} style={{ zIndex: 1101 }}>
        <CircularProgress color="primary" />
      </Backdrop>

      <Container className={classes.container}>
        <Card className={classes.card}>
          <div className={classes.logoOuterContainer}>
            <div className={classes.logoContainer}>
              <img src={logo} alt="Secom" className={classes.logo} />
              <Typography variant="h4" style={{ textAlign: 'center' }}>
                Formular GDPR Secom
              </Typography>
            </div>
          </div>
          <div className={classes.cardContent}>
            <FormControl className={classes.formGrid}>
              <Grid container spacing={1.7}>
                <Grid xs={12} item>
                  <Controller
                    name="cuim"
                    defaultValue={''}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        onChange={onChange}
                        value={value}
                        label={MESSAGES['card.cuim']}
                        variant="outlined"
                        className={classes.input}
                        disabled={true}
                      />
                    )}
                  />
                </Grid>
                <Grid xs={6} item>
                  <Controller
                    name="lastName"
                    defaultValue={''}
                    control={control}
                    rules={formValidations.lastName}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        onChange={onChange}
                        value={value}
                        label={MESSAGES['card.lastName']}
                        variant="outlined"
                        className={classes.input}
                        error={errors['lastName'] && true}
                        helperText={
                          errors['lastName']?.message
                            ? MESSAGES[errors['lastName'].message as string]
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid xs={6} item>
                  <Controller
                    name="firstName"
                    defaultValue={''}
                    control={control}
                    rules={formValidations.firstName}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        onChange={onChange}
                        value={value}
                        label={MESSAGES['card.firstName']}
                        variant="outlined"
                        className={classes.input}
                        error={errors['firstName'] && true}
                        helperText={
                          errors['firstName']?.message
                            ? MESSAGES[errors['firstName'].message as string]
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid xs={6} item>
                  <Controller
                    name="email"
                    defaultValue={''}
                    control={control}
                    rules={formValidations.email}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        onChange={onChange}
                        value={value}
                        label={MESSAGES['card.email']}
                        variant="outlined"
                        className={classes.input}
                        error={errors['email'] && true}
                        helperText={
                          errors['email']?.message
                            ? MESSAGES[errors['email'].message as string]
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid xs={6} item>
                  <Controller
                    name="phone"
                    defaultValue={''}
                    control={control}
                    // rules={formValidations.phoneNumber}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        type="tel"
                        onChange={onChange}
                        value={value}
                        label={MESSAGES['card.phone']}
                        variant="outlined"
                        className={classes.input}
                        error={errors['phone'] && true}
                        helperText={
                          errors['phone']?.message
                            ? MESSAGES[errors['phone'].message as string]
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>
                <Divider
                  style={{ width: '100%', height: '1px', marginTop: '1rem' }}
                />
                <Grid xs={12} item>
                  <Controller
                    name="medicalInviteCommercial"
                    defaultValue={false}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                            size="medium"
                          />
                        }
                        label={
                          <p className={classes.checkLabel}>
                            Invitarea la prezentări comerciale ale produselor
                            SECOM.
                          </p>
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid xs={12} item>
                  <Controller
                    name="medicalInviteMedical"
                    defaultValue={false}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                            size="medium"
                          />
                        }
                        label={
                          <p className={classes.checkLabel}>
                            Invitarea la evenimente de specialitate medicală,
                            precum business lunch-uri cu specialiști din
                            industrie, mese rotunde, conferințe și congrese din
                            domeniul medical.
                          </p>
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid xs={12} item>
                  <Controller
                    name="medicalEmailMarketing"
                    defaultValue={false}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                            size="medium"
                          />
                        }
                        label={
                          <p className={classes.checkLabel}>
                            Primirea de comunicări de marketing pe email privind
                            noutăți din domeniul medical, informări referitoare
                            la produsele SECOM.
                          </p>
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid xs={12} item>
                  <Controller
                    name="medicalSmsMarketing"
                    defaultValue={false}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                            size="medium"
                          />
                        }
                        label={
                          <p className={classes.checkLabel}>
                            Primirea de comunicări de marketing pe SMS privind
                            noutăți din domeniul medical, informări referitoare
                            la produsele SECOM.
                          </p>
                        }
                      />
                    )}
                  />
                </Grid>
                <Divider
                  style={{ width: '100%', height: '1px', marginTop: '1rem' }}
                />
                <Grid xs={12} item>
                  <Controller
                    name="medicalTos"
                    defaultValue={false}
                    control={control}
                    rules={formValidations.tos}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value}
                              onChange={(e) => onChange(e.target.checked)}
                              size="medium"
                            />
                          }
                          label={
                            <p className={classes.checkLabel}>
                              Declar ca am fost informat si am luat cunostinta
                              continutul{' '}
                              <a
                                style={{
                                  textDecoration: 'underline',
                                  color: '#00B5AD',
                                  cursor: 'pointer',
                                }}
                                href={`https://www.secom.ro/pages/declaratie-cu-privire-la-prelucrarea-datelor-cu-caracter-personal`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                prezentei declaratii
                              </a>
                              , drept pentru care mi-am exprimat optiunile
                              privind scopurile prelucrarii datelor mele
                              personale potrivit scopurilor indicate in
                              formularul online.
                            </p>
                          }
                        />
                        {errors['medicalTos'] && (
                          <Typography
                            variant="subtitle1"
                            gutterBottom
                            color="error"
                          >
                            {MESSAGES['card.tos.required']}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Grid>
                <Divider
                  style={{ width: '100%', height: '1px', marginTop: '1rem' }}
                />
                <Grid
                  xs={12}
                  item
                  style={{ marginTop: '1rem', width: '100%' }}
                  ref={canvasParentRef}
                >
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      Semnătura:
                    </Typography>
                    <Button onClick={onResetSignature} variant="text">
                      Resetează semnătura
                    </Button>
                  </div>
                  <SignatureCanvas
                    canvasProps={{
                      width: canvasParentRef.current?.clientWidth,
                      height: '200px',
                      className: 'sigCanvas',
                      style: {
                        border: '1px solid black',
                        width: '100%',
                        height: '200px',
                      },
                    }}
                    ref={signCanvasRef}
                  />
                  {canvasError && (
                    <Typography variant="subtitle1" gutterBottom color="error">
                      Semnătura este obligatorie
                    </Typography>
                  )}
                </Grid>

                <Grid xs={6} item>
                  <Button
                    variant="text"
                    onClick={onReset}
                    color="error"
                    className={classes.formButton}
                  >
                    {MESSAGES['card.reset']}
                  </Button>
                </Grid>
                <Grid xs={6} item>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleSubmit(onSubmit)();
                      validateCanvas();
                    }}
                    className={classes.formButton}
                    style={{ color: 'white' }}
                  >
                    {MESSAGES['card.confirm']}
                  </Button>
                </Grid>
              </Grid>
            </FormControl>
          </div>
        </Card>
      </Container>
      <ErrorModal error={postError} onClose={setPostError.bind(null, '')} />
      <SuccessModal open={success} onClose={onCloseSuccessModal} />
    </>
  );
};

export default CreateMembership;
